'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from '@cc-shopping/utils/function/debounce';
import type { TooltipPosition } from './types';
import { HORIZONTAL_OFFSET, VERTICAL_OFFSET } from './utils';
export const useTooltipPosition = () => {
  const $ = _c(5);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState("bottom");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (!triggerRef.current || !tooltipRef.current) {
        return;
      }
      requestAnimationFrame(() => {
        if (!triggerRef.current || !tooltipRef.current) {
          return;
        }
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const {
          innerWidth: viewportWidth,
          innerHeight: viewportHeight
        } = window;
        const availableSpace = {
          top: triggerRect.top,
          bottom: viewportHeight - triggerRect.bottom,
          left: triggerRect.left,
          right: viewportWidth - triggerRect.right
        };
        const tooltipWidth = tooltipRect.width;
        const tooltipHeight = tooltipRect.height;
        const triggerWidth = triggerRect.width;
        const isOffscreenRight = triggerRect.left + triggerWidth + tooltipWidth + HORIZONTAL_OFFSET > viewportWidth - 0;
        const isOffscreenLeft = triggerRect.left - tooltipWidth - HORIZONTAL_OFFSET < 0;
        const isOffscreenTop = triggerRect.top - tooltipHeight - VERTICAL_OFFSET < 0;
        const isOffscreenBottom = triggerRect.bottom + tooltipHeight + VERTICAL_OFFSET > viewportHeight - 0;
        let preferredPosition = "bottom";
        if (isOffscreenBottom) {
          if (!isOffscreenTop) {
            preferredPosition = "top";
          } else {
            if (!isOffscreenLeft) {
              preferredPosition = "left";
            } else {
              if (!isOffscreenRight) {
                preferredPosition = "right";
              } else {
                const {
                  direction: directionWithMostSpace
                } = Object.entries(availableSpace).reduce(_temp, {
                  direction: "bottom",
                  space: 0
                });
                preferredPosition = directionWithMostSpace;
              }
            }
          }
        }
        setPosition(preferredPosition);
      });
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const updatePosition = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const debouncedUpdatePosition = debounce(updatePosition, 100);
      updatePosition();
      const handleResize = () => {
        debouncedUpdatePosition();
      };
      const handleScroll = () => {
        debouncedUpdatePosition();
      };
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        debouncedUpdatePosition.cancel();
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t2 = [updatePosition];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== position) {
    t3 = {
      triggerRef,
      tooltipRef,
      position
    };
    $[3] = position;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
function _temp(max, t0) {
  const [direction, space] = t0;
  return space > max.space ? {
    direction: direction as TooltipPosition,
    space
  } : max;
}